import { z } from 'zod'
import { FormFields } from '@App/forms/v2/common/form-field'
import { FormFieldTypes } from '@App/enum/form-field-types'
import populateSchema from '@App/forms/v2/helpers/populate-schema'
import { PASSWORD_REGEX } from '@Const/regexp'
import CustomToolTip from '@Components/Tooltips/CustomToolTip'
import React from 'react'

const usernameKey = 'username'
const codeKey = 'code'
const passwordKey = 'password'
const password2Key = 'password2'

const emailSchema = z.object({
    [usernameKey]: z
        .string({
            error: 'Fill in the required field',
        })
        .email()
        .max(255),
})

type EmailType = z.infer<typeof emailSchema>

const emailFormFieldsNoSchema: FormFields<EmailType> = {
    [usernameKey]: {
        name: usernameKey,
        label: 'Email',
        type: FormFieldTypes.TEXT,
    },
}

const emailFormFields: FormFields<EmailType> = populateSchema(emailFormFieldsNoSchema, emailSchema)

const passwordErrorMessage =
    'Minimum 10 characters and at least one upper case, one lower case, one number and one special character'

const changePasswordSchema = z
    .object({
        [codeKey]: z.string({
            error: passwordErrorMessage,
        }),
        [passwordKey]: z.string({
            error: passwordErrorMessage,
        }),
        [password2Key]: z.string({
            error: passwordErrorMessage,
        }),
    })
    .superRefine((changePassword, ctx) => {
        if (changePassword.password) {
            if (!PASSWORD_REGEX.test(changePassword.password)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: passwordErrorMessage,
                    path: [passwordKey],
                })
            }
            if (changePassword.password2 !== changePassword.password) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Passwords must match',
                    path: [password2Key],
                })
            }
        } else {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
            })
        }
        if (!changePassword.code) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
            })
        }
    })

type ChangePasswordType = z.infer<typeof changePasswordSchema>

const changePasswordFormFieldsNoSchema: FormFields<ChangePasswordType> = {
    [codeKey]: {
        name: codeKey,
        label: 'Code',
        type: FormFieldTypes.TEXT,
        tooltip: (
            <CustomToolTip
                type="?"
                place="right"
                info={
                    'A password reset code has been sent to your ' +
                    'email address associated with the account. If ' +
                    "you're still experiencing issues, contact us for " +
                    'assistance.'
                }
            />
        ),
    },
    [passwordKey]: {
        name: passwordKey,
        label: 'Enter a new password',
        type: FormFieldTypes.TEXT,
        inputType: 'password',
        tooltip: (
            <CustomToolTip
                type="?"
                place="right"
                info={
                    'Passwords must contain at least 10 characters ' +
                    'with at least one upper case, one lower case, one ' +
                    'number and one special character.'
                }
            />
        ),
    },
    [password2Key]: {
        name: password2Key,
        label: 'Repeat the password',
        type: FormFieldTypes.TEXT,
        inputType: 'password',
    },
}

const changePasswordFormFields: FormFields<ChangePasswordType> = populateSchema(
    changePasswordFormFieldsNoSchema,
    changePasswordSchema
)

export { emailSchema, EmailType, emailFormFields, changePasswordSchema, ChangePasswordType, changePasswordFormFields }
